import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
  Pagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Inventory2,
} from "@mui/icons-material";
import  {itemService}  from "../../services/itemService";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';
import { useSnackbar } from "notistack";

export const ItemList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemTypeFilter, setItemTypeFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    pages: 0,
  });

  const navigate = useNavigate();
  const { hasAnyRole } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchItems();
    }, 500);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm, itemTypeFilter, unitFilter]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(itemTypeFilter && { itemType: itemTypeFilter }),
        ...(unitFilter && { unit: unitFilter }),
      };

      const response = await itemService.getItems(params);
      const { items: itemsData, pagination: paginationData } = response.data.data;
      
      setItems(itemsData);
      setPagination(prev => ({
        ...prev,
        page:  paginationData.page || prev.page,
        limit: paginationData.limit || prev.limit,
        total: paginationData.total,
        pages: paginationData.pages
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching items", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleView = () => {
    if (selectedItem) {
      navigate(`/hospital/hospital-inventory/items/${selectedItem._id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedItem) {
      navigate(`/hospital/hospital-inventory/items/${selectedItem._id}/edit`);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    try {
      await itemService.deleteItem(selectedItem._id);
      enqueueSnackbar("Item deleted successfully", { variant: "success" });
      fetchItems();
    } catch (error) {
      enqueueSnackbar("Error deleting item", { variant: "error" });
    }
    handleMenuClose();
  };

  const getItemTypeColor = (itemType) => {
    const colors = {
      consumable: "primary",
      equipment: "secondary",
      raw_material: "info",
      finished_good: "success",
      other: "default",
    };
    return colors[itemType] || "default";
  };

  const getItemTypeIcon = (itemType) => {
    const icons = {
      consumable: "🔧",
      equipment: "⚙️",
      raw_material: "📦",
      finished_good: "✅",
      other: "📋",
    };
    return icons[itemType] || "📋";
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Items
        </Typography>
        {hasAnyRole(["admin", "inventory_manager"]) && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/hospital/hospital-inventory/items/new")}
          >
            Add Item
          </Button>
        )}
      </Box>

      {/* Search and Filter Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search items by name, brand, or manufacturer..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Item Type</InputLabel>
                <Select
                  value={itemTypeFilter}
                  onChange={(e) => {
                    setItemTypeFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  label="Item Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="consumable">Consumable</MenuItem>
                  <MenuItem value="equipment">Equipment</MenuItem>
                  <MenuItem value="raw_material">Raw Material</MenuItem>
                  <MenuItem value="finished_good">Finished Good</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={unitFilter}
                  onChange={(e) => {
                    setUnitFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  label="Unit"
                >
                  <MenuItem value="">All Units</MenuItem>
                  <MenuItem value="pieces">Pieces</MenuItem>
                  <MenuItem value="kg">Kilograms</MenuItem>
                  <MenuItem value="liters">Liters</MenuItem>
                  <MenuItem value="boxes">Boxes</MenuItem>
                  <MenuItem value="meters">Meters</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Total: {pagination.total} items
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Items Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {items.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item._id}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "0.3s",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 3 },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h4">
                          {getItemTypeIcon(item.itemType)}
                        </Typography>
                        <Box>
                          <Typography variant="h6" fontWeight="bold" noWrap>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.brand}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, item)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {item.manufacturer} • {item.unit}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                      <Chip
                        label={item.itemType.replace("_", " ").toUpperCase()}
                        size="small"
                        color={getItemTypeColor(item.itemType)}
                      />
                      <Chip
                        label={item.unit}
                        size="small"
                        variant="outlined"
                      />
                      {item.category && (
                        <Chip
                          label={item.category}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {item.description && item.description.length > 100
                        ? `${item.description.substring(0, 100)}...`
                        : item.description}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        By {item.manufacturer}
                      </Typography>
                      <Chip
                        label={item.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={item.isActive ? "success" : "default"}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No Results */}
          {items.length === 0 && !loading && (
            <Card sx={{ textAlign: "center", py: 6 }}>
              <CardContent>
                <Inventory2 sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No items found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {searchTerm || itemTypeFilter || unitFilter
                    ? "Try adjusting your search or filters"
                    : "Get started by adding your first item"}
                </Typography>
                {hasAnyRole(["admin", "inventory_manager"]) && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate("/items/new")}
                  >
                    Add First Item
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem onClick={handleEdit}>
            <Edit sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {hasAnyRole(["admin"]) && (
          <MenuItem onClick={handleDelete}>
            <Delete sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
